import { uniq } from 'lodash'
import { graphql } from 'app/generated/gql'
import { tagValuesFetcher } from 'app/lib/fetchers/tagValuesFetcher'
import { gqlQuery } from 'app/lib/graphqlClient'
import { getActivityBuiltInTags, getContactBuiltInTags } from 'lib/getBuiltInTags'
import { logError } from 'lib/logging'

const TAG_COLORS_FILTER_QUERY = graphql(`
  query TagColorsFilter($search: String) {
    tagColors(search: $search) {
      hits {
        name
      }
    }
  }
`)

/** `tagValues` keys off the Elasticsearch index name, where `tagColors` keys off the model name. */
const TAG_VALUES_INDEX = { Contact: `contacts`, Activity: `activities` } as const

/**
 * Split the typed text into the tokens Elasticsearch indexes for an analyzed text field.
 * The standard analyzer drops `: / -` and other punctuation, so "Insurance: Out" and
 * "10/14" become `insurance out` and `10 14`. Keeping only letters and digits also means
 * no query_string syntax can leak in from user input.
 */
export function tagSearchTokens(query: string | undefined): string[] {
  return (query ?? ``)
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
}

/**
 * query_string clause matching every token as a substring of the field, in any order.
 * `default_operator: and` on the server makes the space between tokens an AND, so
 * "New Blue" no longer matches "New Chat".
 */
export function tagSearchClause(field: string, tokens: string[]): string | undefined {
  if (!tokens.length) return undefined
  return `${field}:(${tokens.map((token) => `*${token}*`).join(` `)})`
}

/**
 * Tag filter options: every tag actually in use on the entity, plus configured TagColor
 * rows and the built-in defaults.
 *
 * The server narrows both sources by token so each keystroke stays small. The final
 * substring check happens here because a token query cannot express order or the
 * punctuation between words: "ce: out" must match "Insurance: Out of Network".
 */
export async function tagFilterOptionsFetcher(
  entity: `Contact` | `Activity`,
  params: { query: string; [key: string]: string },
): Promise<string[]> {
  const tokens = tagSearchTokens(params.query)
  const colorsClause = tagSearchClause(`name`, tokens)
  const colorsSearch = colorsClause ? `entity:${entity} AND ${colorsClause}` : `entity:${entity}`
  const [colors, inUse] = await Promise.all([
    gqlQuery<{ tagColors: { hits: { name: string }[] } }>(TAG_COLORS_FILTER_QUERY, { search: colorsSearch }),
    tagValuesFetcher(TAG_VALUES_INDEX[entity], { query: tagSearchClause(`tags`, tokens) ?? `` }),
  ])
  if (colors.error) logError(`tagFilterOptionsFetcher failed`, colors.error.message)
  const builtIns = entity === `Contact` ? getContactBuiltInTags() : getActivityBuiltInTags()
  const names = uniq([...(colors.data?.tagColors?.hits?.map((tag) => tag.name) ?? []), ...inUse, ...builtIns])
  const query = params.query?.trim().toLowerCase()
  return (query ? names.filter((name) => name.toLowerCase().includes(query)) : names).sort((a, b) => a.localeCompare(b))
}
